
import React from 'react';
import { AppMode, Language, User } from '../types';
import { TRANSLATIONS } from '../../constants';

interface NavRailItemsProps {
  currentMode: AppMode;
  onSwitchMode: (mode: AppMode) => void;
  onClose: () => void;
  language: Language;
  user: User | null;
}

const NavRailItems: React.FC<NavRailItemsProps> = ({ currentMode, onSwitchMode, onClose, language, user }) => {
  const t = TRANSLATIONS[language];
  const isRtl = language === 'ar';
  
  // New privileged check: SuperAdmin, Super, or Admin all get dashboard access
  const canAccessAdmin = user?.role === 'superadmin' || user?.role === 'super' || user?.role === 'admin' || user?.email === 'admin@lahja.app';

  const navItems: { mode: AppMode; icon: string; label: string; protected?: boolean }[] = [
    { mode: 'chat', icon: 'fa-message', label: t.chatRoom },
    { mode: 'live', icon: 'fa-microphone-lines', label: t.liveMode },
    { mode: 'services', icon: 'fa-cubes', label: t.servicesMode },
    { mode: 'flows', icon: 'fa-diagram-project', label: t.flowsMode },
    { mode: 'terminal', icon: 'fa-terminal', label: t.terminalMode },
    { mode: 'robot', icon: 'fa-robot', label: t.robotMode },
    { mode: 'integrations', icon: 'fa-network-wired', label: t.integrationsMode },
    { mode: 'profile', icon: 'fa-chart-pie', label: t.profileMode },
    { mode: 'docs', icon: 'fa-book', label: t.docsMode },
    { mode: 'admin', icon: 'fa-user-shield', label: t.adminMode, protected: true },
  ];

  return (
    <div className="flex-1 flex flex-col gap-4 w-full px-3 overflow-y-auto custom-scrollbar no-scrollbar">
      {navItems.map((item) => {
        if (item.protected && !canAccessAdmin) return null;
        
        return (
          <button
            key={item.mode}
            onClick={() => { onSwitchMode(item.mode); onClose(); }}
            className={`
              group relative flex items-center justify-center w-full aspect-square rounded-xl transition-all duration-300
              ${currentMode === item.mode 
                ? 'bg-emerald-500/10 text-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.2)]' 
                : 'text-slate-500 hover:bg-white/5 hover:text-slate-300'}
            `}
          >
            <i className={`fas ${item.icon} text-lg transition-transform group-hover:scale-110`}></i>
            
            {/* Tooltip */}
            <div className={`
              hidden lg:block absolute ${isRtl ? 'left-full ml-4' : 'right-full mr-4'} top-1/2 -translate-y-1/2 
              bg-slate-800 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 
              pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50 shadow-xl border border-white/5
            `}>
              {item.label}
              <div className={`absolute top-1/2 -translate-y-1/2 ${isRtl ? 'right-full border-r-slate-800' : 'left-full border-l-slate-800'} border-4 border-transparent`}></div>
            </div>

            {currentMode === item.mode && (
              <div className={`absolute ${isRtl ? 'right-0' : 'left-0'} top-1/2 -translate-y-1/2 w-1 h-8 bg-emerald-500 rounded-full`}></div>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default NavRailItems;
